import React, { useEffect, useState } from "react";
import LoadingSpinner from '../../ui/LoadingSpinner';
import ChartBase from '../ChartBase';
import { chartData } from '../data';
import { getCustomersWithAlarms } from '../../../services/bureauManagement/bureauManagement.service';
import { useAuthContext } from '../../../contexts/useAuthContext';
import type { ApexOptions } from 'apexcharts';

const NO_DATA_DELAY_MS = 300;

interface SiteAlarm {
  site: string;
  acknowledged: number;
  unacknowledged: number;
}

interface Props {
  data?: SiteAlarm[];
  title?: string;
  subtitle?: string;
}

const SiteColumnChart: React.FC<Props> = ({
  data,
  title = 'Active Alarms by Site',
  subtitle = 'Active Acknowledged vs Unacknowledged by Site',
}) => {
  const { isAuthenticated, isLoading: authLoading } = useAuthContext();
  const authReady = isAuthenticated && !authLoading;
  const [source, setSource] = useState<SiteAlarm[] | undefined>(data);
  const [error, setError] = useState<string | null>(null);
  const [showNoData, setShowNoData] = useState(false);

  useEffect(() => {
    if (data) {
      setSource(data);
      return;
    }
    if (!authReady) { return; }

    let canceled = false;

    getCustomersWithAlarms()
      .then(res => {
        if (canceled) { return; }
        if (res?.success && res.data?.customers?.length) {
          // Collect all site-type nodes from the customer tree
          const sites: SiteAlarm[] = [];
          const traverse = (nodes: typeof res.data.customers) => {
            for (const node of nodes) {
              if (node.type === 'site' && (node.activeAck > 0 || node.activeNotAck > 0)) {
                sites.push({
                  site: node.title,
                  acknowledged: node.activeAck ?? 0,
                  unacknowledged: node.activeNotAck ?? 0,
                });
              }
              if (node.children?.length) { traverse(node.children); }
            }
          };
          traverse(res.data.customers);
          setSource(sites.length > 0 ? sites : undefined);
        } else {
          setSource(undefined);
          if (!res?.success) { setError('Failed to load site alarm data'); }
        }
      })
      .catch(() => { if (!canceled) { setError('Failed to load site alarm data'); } });

    return () => { canceled = true; };
  }, [data, authReady]);

  useEffect(() => {
    if (source) {
      setShowNoData(false);
      return undefined;
    }
    const t = setTimeout(() => setShowNoData(true), NO_DATA_DELAY_MS);
    return () => clearTimeout(t);
  }, [source]);

  const categories = source ? source.map(d => d.site) : (Array.isArray(chartData.options?.xaxis?.categories) ? chartData.options.xaxis.categories as string[] : []);
  const series: ApexOptions['series'] = source
    ? [
        { name: 'Acknowledged', data: source.map(d => d.acknowledged ?? 0) },
        { name: 'Unacknowledged', data: source.map(d => d.unacknowledged ?? 0) },
      ]
    : [];

  const options: ApexOptions = {
    ...chartData.options,
    chart: { ...(chartData.options?.chart ?? {}), id: 'siteColumnChart' },
    xaxis: { ...(chartData.options?.xaxis ?? {}), categories },
    tooltip: { ...(chartData.options?.tooltip ?? {}), shared: true, intersect: false },
  };

  const renderContent = () => {
    const safeSeries = (series ?? []) as NonNullable<ApexOptions['series']>;
    if (source && safeSeries.length > 0) {
      return <ChartBase options={options} series={safeSeries} type="bar" height={350} width="100%" id="siteColumnChart" />;
    }
    if (error) {
      return <div className="status__error">{error}</div>;
    }
    if (showNoData) {
      return <div className="table-responsive-nodata-text">No Data</div>;
    }
    return <LoadingSpinner message="Loading chart data…" />;
  };

  return (
    <div className="card">
      <div className="charts">
        <h3>{title}</h3>
        <h5>{subtitle}</h5>
        {renderContent()}
      </div>
    </div>
  );
};

export default SiteColumnChart;
